import { scaleLinear } from 'd3-scale';
import { isNumber, isUndefined } from 'lodash-es';

import { getSymlogScale } from './symlog-scale.js';
import type {
  MaxScaleBoundary,
  MinMaxConfig,
  MinScaleBoundary,
} from '../types/min-max-config.js';
import { MinMax } from '../types/min-max.js';
import { AxisScaleType } from '../types/scales.js';

export const buildScale = (
  scaleType: AxisScaleType,
  range: number[],
  dataBoundaries: MinMax,
  configMinMax?: Partial<MinMaxConfig>,
) => {
  const minMaxOptions = {
    niceMin: isDefaultBoundary(configMinMax?.min),
    niceMax: isDefaultBoundary(configMinMax?.max),
  };
  const { min, max } = getMinMax(
    scaleType,
    dataBoundaries,
    configMinMax,
    minMaxOptions,
  );

  return getScale(scaleType, [min, max], range);
};

export const getScale = (
  scaleType: AxisScaleType,
  domain: number[],
  range: number[] = [0, 1],
) =>
  (scaleType === 'linear' ? scaleLinear() : getSymlogScale())
    .domain(domain)
    .range(range);

export type MinMaxOptions = {
  niceMin: boolean;
  niceMax: boolean;
};

const MIN_MAX_DEFAULT_OPTIONS = {
  niceMin: true,
  niceMax: true,
};

export const getMinMax = (
  scaleType: AxisScaleType,
  dataBoundaries: MinMax,
  configMinMax: Partial<MinMaxConfig> | undefined,
  options: MinMaxOptions = MIN_MAX_DEFAULT_OPTIONS,
): MinMax => {
  const baselineDomain = getBaselineDomain(scaleType, dataBoundaries);

  let min = getScaleBoundary(
    dataBoundaries.min,
    baselineDomain.min,
    configMinMax?.min,
  );
  let max = getScaleBoundary(
    dataBoundaries.max,
    baselineDomain.max,
    configMinMax?.max,
  );

  const niceScale = getScale(scaleType, [min, max]).nice().domain();
  const scale = getScale(scaleType, [min, max]).domain();

  const [defaultMin, defaultMax] = scale;
  const [niceMin, niceMax] = niceScale;

  min = defaultMin;
  max = defaultMax;

  if (options.niceMin) {
    min = niceMin;
  }

  if (options.niceMax) {
    max = niceMax;
  }

  return { min, max };
};

// https://dt-rnd.atlassian.net/wiki/spaces/DAT/pages/777584781/Axis+baselines+for+XY+charts#Baseline
export const getBaselineDomain = (
  scaleType: AxisScaleType,
  dataBoundaries: MinMax,
): MinMax => {
  let [baselineMin, baselineMax] = [0, 0];

  if (scaleType === 'log') {
    baselineMin = 1;
    baselineMax = -1;
  }

  const baselineDomainMin =
    dataBoundaries.min > baselineMin ? baselineMin : dataBoundaries.min;
  const baselineDomainMax =
    dataBoundaries.max < baselineMax ? baselineMax : dataBoundaries.max;

  return { min: baselineDomainMin, max: baselineDomainMax };
};

export const getScaleBoundary = (
  dataBoundary: number,
  baselineBoundary: number,
  scaleBoundary?: MinScaleBoundary | MaxScaleBoundary,
): number => {
  if (isNumber(scaleBoundary)) {
    return scaleBoundary;
  }

  if (isDefaultBoundary(scaleBoundary)) {
    return baselineBoundary;
  }

  return dataBoundary;
};

export const isDefaultBoundary = (
  boundary?: MinScaleBoundary | MaxScaleBoundary,
): boundary is 'auto' => isUndefined(boundary) || boundary === 'auto';

export const isDataBoundary = (
  boundary?: MinScaleBoundary | MaxScaleBoundary,
): boundary is 'data-min' | 'data-max' =>
  !isUndefined(boundary) &&
  (boundary === 'data-min' || boundary === 'data-max');
